import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import AuthLayout from '../../layouts/AuthLayout';
import { useAuth } from '../../../lib/auth';
import { AgeBandStep } from '../../../components/plan/AgeBandStep';
import { QuestionnaireStep } from '../../../components/plan/QuestionnaireStep';
import { AgeBand, PlanAnswers } from '../../../lib/plan/types';
import { generatePlan } from '../../../lib/plan/usePlan';
import { useSWRConfig } from 'swr';
import { PLAN_KEY } from '../../../lib/plan/usePlan';

// Minimální doba zobrazení skládání plánu, aby výsledek nenaskočil skokem
// a bylo vidět, že se plán opravdu „skládá na míru".
const MIN_GENERATING_MS = 10000;
const PHASE_SWITCH_MS = 5000;

export default function PlanQuestionnairePage() {
    useAuth({ middleware: 'auth', redirectTo: '/', tmp: 'PlanQuestionnairePage' });
    const navigate = useNavigate();
    const { mutate } = useSWRConfig();

    const [ageBand, setAgeBand] = useState<AgeBand | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [phase, setPhase] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const phaseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined
    );

    useEffect(() => () => clearTimeout(phaseTimer.current), []);

    async function handleSubmit(answers: PlanAnswers) {
        if (!ageBand) return;
        setIsGenerating(true);
        setPhase(0);
        setError(null);
        phaseTimer.current = setTimeout(() => setPhase(1), PHASE_SWITCH_MS);
        try {
            const [plan] = await Promise.all([
                generatePlan(answers, ageBand),
                new Promise((resolve) =>
                    setTimeout(resolve, MIN_GENERATING_MS)
                ),
            ]);
            await mutate(PLAN_KEY, plan, { revalidate: false });
            navigate('/plan-na-miru/vysledky');
        } catch {
            clearTimeout(phaseTimer.current);
            setError('Plán se nepodařilo sestavit. Zkuste to prosím znovu.');
            setIsGenerating(false);
        }
    }

    return (
        <AuthLayout>
            <main className="mx-auto box-content max-w-2xl px-4 py-9 sm:px-8 md:px-10">
                {isGenerating ? (
                    <div className="flex flex-col items-center gap-4 py-24 text-center">
                        <div className="h-10 w-10 animate-spin rounded-full border-4 border-cloud-300 border-t-product-500" />
                        {phase === 0 ? (
                            <>
                                <h2>Vyhodnocujeme odpovědi…</h2>
                                <p className="text-ink-800">
                                    Zjišťujeme, co vaší třídě nejvíc pomůže.
                                </p>
                            </>
                        ) : (
                            <>
                                <h2>Skládáme plán na míru…</h2>
                                <p className="text-ink-800">
                                    Vybíráme a řadíme cvičení pro vaši třídu.
                                </p>
                            </>
                        )}
                    </div>
                ) : ageBand === null ? (
                    <AgeBandStep onSelect={setAgeBand} />
                ) : (
                    <>
                        {error && <p className="mb-4 font-medium text-red-500">{error}</p>}
                        <QuestionnaireStep onSubmit={handleSubmit} />
                    </>
                )}
            </main>
        </AuthLayout>
    );
}
